export const suggestDuty = (
    previousSelection: string,
    availableOptions: string[],
    currentTime: Date = new Date()
): string | null => {
    // Extract the numeric part from the previous selection
    const numberMatch = previousSelection.match(/^(?:[nN])?(\d+)/i)
    if (!numberMatch) {
        return null
    }
    const targetNumber = numberMatch[1] // Just the numeric part

    // Get current London time
    const londonTime = new Date(currentTime.toLocaleString('en-US', { timeZone: 'Europe/London' }))
    const hours = londonTime.getHours()
    const dayOfWeek = londonTime.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    // Determine current time period
    let currentTimePeriod: string
    if (hours < 12) {
        currentTimePeriod = 'e' // Early (before 12:00)
    } else if (hours < 19) {
        currentTimePeriod = 'l' // Late (12:00 to 19:00)
    } else {
        currentTimePeriod = 'n' // Night (19:00 to 00:00)
    }

    // Determine current day type
    const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
    const currentDayName = dayNames[dayOfWeek]

    let currentDayType: string
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        currentDayType = 'mf' // Monday to Friday
    } else if (dayOfWeek === 6) {
        currentDayType = 'sat' // Saturday
    } else {
        currentDayType = 'sun' // Sunday
    }

    // Filter options that match the target number (numeric part only)
    const matchingNumberOptions = availableOptions.filter((option) => {
        const optionNumberMatch = option.match(/^(?:[nN])?(\d+)/i)
        return optionNumberMatch && optionNumberMatch[1] === targetNumber
    })

    if (matchingNumberOptions.length === 0) {
        return null
    }

    // Parse and validate options
    const validOptions = matchingNumberOptions
        .map((option) => {
            const parts = option.trim().split(/\s+/)
            if (parts.length !== 2) return null

            const [timeCode, dayCode] = parts

            // Extract time period - it's the first character for night duties (n/N),
            // or the last character for early/late duties (e/E, l/L)
            let timePeriod: string
            if (timeCode.toLowerCase().startsWith('n')) {
                timePeriod = 'n'
            } else {
                timePeriod = timeCode.slice(-1).toLowerCase()
            }

            const dayType = dayCode.toLowerCase()

            // Validate time period
            if (!['e', 'l', 'n'].includes(timePeriod)) return null

            // Validate day type
            const validDayTypes = ['mf', 'sat', 'sun', 'mon', 'tue', 'wed', 'thu', 'fri']
            if (!validDayTypes.includes(dayType)) return null

            return {
                original: option,
                timePeriod,
                dayType,
                number: timeCode.match(/^(?:[nN])?(\d+)/i)?.[1] || '',
            }
        })
        .filter((option) => option !== null)

    // Filter by current time period
    const timeMatchingOptions = validOptions.filter((option) => option.timePeriod === currentTimePeriod)

    if (timeMatchingOptions.length === 0) {
        return null // No options match current time period
    }

    // Filter by current day - prefer specific day over general day types
    // Priority: specific day (THU) > MF (if weekday) > SAT/SUN (if weekend)
    let dayMatchingOptions = timeMatchingOptions.filter((option) => option.dayType === currentDayName)

    // If no specific day match, try general day types
    if (dayMatchingOptions.length === 0) {
        if (currentDayType === 'mf') {
            dayMatchingOptions = timeMatchingOptions.filter((option) => option.dayType === 'mf')
        } else if (currentDayType === 'sat') {
            dayMatchingOptions = timeMatchingOptions.filter((option) => option.dayType === 'sat')
        } else if (currentDayType === 'sun') {
            dayMatchingOptions = timeMatchingOptions.filter((option) => option.dayType === 'sun')
        }
    }

    if (dayMatchingOptions.length === 0) {
        return null // No options match current day
    }

    // Return the first matching option (they should all be equivalent at this point)
    return dayMatchingOptions[0].original
}
